import React, { useContext, useEffect, useState } from "react"

import Paper from "@material-ui/core/Paper"
import Avatar from "@material-ui/core/Avatar"
import Chip from "@material-ui/core/Chip"
import Container from "@material-ui/core/Container"
import Grid from "@material-ui/core/Grid"
import { makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography";
import { useHistory } from "react-router-dom";

import useTickets from "../../hooks/useTickets"
import api from "../../services/api";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";

import { i18n } from "../../translate/i18n";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

import Chart from "./Chart"

const getAssignmentMode = mode =>
	mode === "normal" ? "normal" : "multiagent";

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	fixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 240,
	},
	customFixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 120,
	},
	reservationListPaper: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.5),
	},
	reservationListHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		flexWrap: "wrap",
	},
	reservationRow: {
		minHeight: 48,
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(0.75),
		padding: theme.spacing(0.75, 1),
		marginBottom: theme.spacing(0.75),
		border: "2px solid rgba(0, 0, 0, 0.12)",
		borderRadius: 6,
		background: "#fff",
		cursor: "pointer",
		transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
		"&:hover": {
			borderColor: theme.palette.primary.light,
			boxShadow: "0 2px 8px rgba(63, 81, 181, 0.12)",
			transform: "translateY(-1px)",
		},
	},
	reservationMain: {
		minWidth: 0,
		flex: 1,
	},
	reservationTitle: {
		fontSize: 14,
		fontWeight: 600,
		color: theme.palette.text.primary,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
	},
	reservationMeta: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(1),
	},
	reservationNotes: {
		whiteSpace: "pre-wrap",
		marginTop: theme.spacing(1),
	},
	chip: {
		height: 22,
		maxWidth: 220,
	},
	censoredPhoto: {
		filter: "blur(6px)",
	},
	emptyText: {
		color: theme.palette.text.secondary,
	},
	meetingTime: {
		fontWeight: 700,
		color: theme.palette.primary.main,
		minWidth: 82,
	},
}))

const reservationStatusLabels = {
	pending: "Pendiente",
	confirmed: "Confirmada",
	cancelled: "Cancelada",
	rescheduled: "Reprogramada",
	completed: "Completada",
	no_show: "No asistio",
};

const isSameDay = (dateA, dateB) =>
	dateA.getFullYear() === dateB.getFullYear() &&
	dateA.getMonth() === dateB.getMonth() &&
	dateA.getDate() === dateB.getDate();

const addDays = (date, amount) => {
	const next = new Date(date);
	next.setDate(next.getDate() + amount);
	return next;
};

const pad = value => String(value).padStart(2, "0");

const getDatePartsInTimeZone = (value, timeZone) => {
	if (!value || !timeZone) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;

	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).formatToParts(date);

	const values = parts.reduce((acc, part) => {
		if (part.type !== "literal") acc[part.type] = Number(part.value);
		return acc;
	}, {});

	return {
		year: values.year,
		month: values.month,
		day: values.day,
	};
};

const toDateInputInTimeZone = (value, timeZone) => {
	const parts = getDatePartsInTimeZone(value, timeZone);
	if (!parts) return "";
	return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

const formatMeetingTime = (value, timeZone) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleTimeString("es-CR", {
		timeZone,
		hour: "2-digit",
		minute: "2-digit",
	});
};

const isReservationToday = reservation => {
	const timeZone = reservation.service?.timeZone;
	if (!timeZone) {
		const startsAt = new Date(reservation.startsAt);
		return !Number.isNaN(startsAt.getTime()) && isSameDay(startsAt, new Date());
	}

	return (
		toDateInputInTimeZone(reservation.startsAt, timeZone) ===
		toDateInputInTimeZone(new Date(), timeZone)
	);
};

const Dashboard = () => {
	const classes = useStyles()
	const history = useHistory();
	const [todayReservations, setTodayReservations] = useState([]);

	const { user } = useContext(AuthContext);
	const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
	const assignmentModeStorageKey = `ticketAssignmentMode:${
		user?.companyId || user?.company?.id || "default"
	}`;
	const [assignmentMode, setAssignmentMode] = useState(() =>
		getAssignmentMode(localStorage.getItem(assignmentModeStorageKey))
	);
	const isNormalMode = assignmentMode === "normal";
	var userQueueIds = [];

	if (user.queues && user.queues.length > 0) {
		userQueueIds = user.queues.map(q => q.id);
	}

	const GetTickets = (status, showAll, withUnreadMessages) => {

		const { count } = useTickets({
			status: status,
			showAll: showAll,
			withUnreadMessages: withUnreadMessages,
			queueIds: JSON.stringify(userQueueIds)
		});
		return count;
	}

	const openTicketsCount = GetTickets("open", "true", "false");
	const pendingTicketsCount = GetTickets("pending", "true", "false");
	const closedTicketsCount = GetTickets("closed", "true", "false");

	useEffect(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		if (storedMode) {
			setAssignmentMode(getAssignmentMode(storedMode));
		}

		const handleAssignmentModeChanged = event => {
			setAssignmentMode(getAssignmentMode(event?.detail?.mode));
		};

		window.addEventListener(
			"ticketAssignmentModeChanged",
			handleAssignmentModeChanged
		);

		return () => {
			window.removeEventListener(
				"ticketAssignmentModeChanged",
				handleAssignmentModeChanged
			);
		};
	}, [assignmentModeStorageKey]);

	useEffect(() => {
		let mounted = true;

		const fetchTodayReservations = async () => {
			try {
				const start = addDays(new Date(), -2);
				const end = addDays(new Date(), 2);
				const { data } = await api.get("/appointments/reservations", {
					params: {
						start: start.toISOString(),
						end: end.toISOString(),
					},
				});

				const reservations = (data.reservations || [])
					.filter(isReservationToday)
					.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));

				if (mounted) setTodayReservations(reservations);
			} catch (err) {
				toastError(err);
			}
		};

		fetchTodayReservations();

		return () => {
			mounted = false;
		};
	}, []);

	const renderReservation = reservation => {
		const serviceTimeZone = reservation.service?.timeZone;
		const customerName = privacyCensorSettings.contactNames
			? "Contacto privado"
			: reservation.customerName ||
			  reservation.contact?.name ||
			  reservation.title ||
			  "Sin nombre";

		return (
			<Paper
				key={reservation.id}
				elevation={0}
				className={classes.reservationRow}
				onClick={() => history.push("/appointments")}
			>
				<Typography className={classes.meetingTime}>
					{formatMeetingTime(reservation.startsAt, serviceTimeZone)}
				</Typography>
				<div className={classes.reservationMain}>
					<Typography className={classes.reservationTitle}>{customerName}</Typography>
					<div className={classes.reservationMeta}>
						{reservation.service?.name && (
							<Chip
								size="small"
								className={classes.chip}
								label={`Servicio: ${reservation.service.name}`}
							/>
						)}
						<Chip
							size="small"
							className={classes.chip}
							label={reservationStatusLabels[reservation.status] || reservation.status}
						/>
						{reservation.contact && (
							<Chip
								size="small"
								className={classes.chip}
								avatar={
									<Avatar
										src={
											privacyCensorSettings.contactPhotos
												? undefined
												: reservation.contact.profilePicUrl
										}
										className={
											privacyCensorSettings.contactPhotos
												? classes.censoredPhoto
												: undefined
										}
									>
										{privacyCensorSettings.contactNames
											? undefined
											: reservation.contact.name?.[0]}
									</Avatar>
								}
								label={
									privacyCensorSettings.contactNames
										? "Contacto privado"
										: reservation.contact.name
								}
								title={
									privacyCensorSettings.contactNames
										? "Telefono privado"
										: reservation.contact.number
								}
							/>
						)}
						{reservation.connection?.name && (
							<Chip
								size="small"
								className={classes.chip}
								label={`Conexion: ${reservation.connection.name}`}
							/>
						)}
					</div>
					{reservation.notes && (
						<Typography variant="body2" className={classes.reservationNotes}>
							{privacyCensorSettings.conversations
								? "Nota privada"
								: reservation.notes}
						</Typography>
					)}
				</div>
			</Paper>
		);
	};

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				<Grid container spacing={3}>
					<Grid item xs={6} sm={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.inAttendance.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{openTicketsCount}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					{!isNormalMode && (
						<Grid item xs={6} sm={4}>
							<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
								<Typography component="h3" variant="h6" color="primary" paragraph>
									{i18n.t("dashboard.messages.waiting.title")}
								</Typography>
								<Grid item>
									<Typography component="h1" variant="h4">
										{pendingTicketsCount}
									</Typography>
								</Grid>
							</Paper>
						</Grid>
					)}
					<Grid item xs={6} sm={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.closed.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{closedTicketsCount}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={12}>
						<Paper className={classes.fixedHeightPaper}>
							<Chart />
						</Paper>
					</Grid>
					<Grid item xs={12}>
						<Paper className={classes.reservationListPaper}>
							<div className={classes.reservationListHeader}>
								<Typography component="h2" variant="h6" color="primary">
									Reuniones de hoy
								</Typography>
								<Chip size="small" label={`${todayReservations.length} reuniones`} />
							</div>
							{todayReservations.length ? (
								todayReservations.map(renderReservation)
							) : (
								<Typography variant="body2" className={classes.emptyText}>
									No hay reuniones programadas para hoy.
								</Typography>
							)}
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	)
}

export default Dashboard

